/**
 * AgentSkillsSelector Component
 *
 * Multi-select checkbox list for picking which skills an agent can invoke.
 * Used inside the agent form's Tools tab.
 */

import React, { useMemo } from 'react';
import { useSkills } from '../hooks/useSkills';
import { Checkbox } from '@/shared/components/ui/Checkbox';
import { Icon } from '@/shared/icons';
import type { WorkflowResponse } from '@/modules/workflows/types';

export interface AgentSkillsSelectorProps {
  selectedSkillIds: string[];
  onChange: (skillIds: string[]) => void;
}

export const AgentSkillsSelector: React.FC<AgentSkillsSelectorProps> = ({
  selectedSkillIds,
  onChange,
}) => {
  // Fetch all active skills (large limit, no pagination in selector)
  const { data, isLoading, error } = useSkills({ status: 'active', limit: 100 });

  const skills: WorkflowResponse[] = useMemo(() => data?.items || [], [data]);
  const selectedSet = useMemo(() => new Set(selectedSkillIds), [selectedSkillIds]);

  const toggleSkill = (skillId: string) => {
    if (selectedSet.has(skillId)) {
      onChange(selectedSkillIds.filter((id) => id !== skillId));
    } else {
      onChange([...selectedSkillIds, skillId]);
    }
  };

  if (isLoading) {
    return (
      <div
        className="rounded-lg p-4 text-sm"
        style={{
          backgroundColor: 'var(--theme-input-background)',
          border: '1px solid var(--theme-input-border)',
          color: 'var(--theme-muted-text)',
        }}
      >
        Carregando skills...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-lg p-4 text-sm"
        style={{
          backgroundColor: 'var(--theme-input-background)',
          border: '1px solid var(--theme-input-border)',
          color: 'var(--theme-error-color)',
        }}
      >
        Erro ao carregar skills: {(error as Error).message}
      </div>
    );
  }

  if (skills.length === 0) {
    return (
      <div
        className="rounded-lg p-6 text-center"
        style={{
          backgroundColor: 'var(--theme-input-background)',
          border: '1px dashed var(--theme-input-border)',
        }}
      >
        <Icon
          name="zap"
          className="h-8 w-8 mx-auto mb-2"
          style={{ color: 'var(--theme-muted-text)', opacity: 0.5 }}
        />
        <p className="text-sm mb-3" style={{ color: 'var(--theme-muted-text)' }}>
          Nenhuma skill ativa encontrada.
        </p>
        <a
          href="/automation/skills/new"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm underline"
          style={{ color: 'var(--theme-primary)' }}
        >
          Criar skill
        </a>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg"
      style={{
        backgroundColor: 'var(--theme-input-background)',
        border: '1px solid var(--theme-input-border)',
      }}
    >
      {skills.map((skill, index) => {
        const skillInput = skill.definition?.nodes?.find((n) => n.type === 'skill_input');
        const description =
          (skillInput?.data?.config as { description?: string } | undefined)?.description ||
          skill.description ||
          '';
        const isChecked = selectedSet.has(skill.id);
        return (
          <div
            key={skill.id}
            className={index > 0 ? 'border-t p-3' : 'p-3'}
            style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
          >
            <Checkbox
              checked={isChecked}
              onChange={() => toggleSkill(skill.id)}
              label={skill.name}
              description={description || undefined}
            />
          </div>
        );
      })}
    </div>
  );
};

export default AgentSkillsSelector;
